import { Badge, createStyles, Divider, Flex, Group, Skeleton, Stack } from '@mantine/core';
import { useContext, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useQueryClient } from '@tanstack/react-query';

import Dialog from '~/domains/design-system/components/Dialog/Dialog';
import DialogBody from '~/domains/design-system/components/Dialog/DialogBody/DialogBody';
import DialogContainer from '~/domains/design-system/components/Dialog/DialogContainer';
import { Text } from '~/domains/design-system/Text';
import { Title } from '~/domains/design-system/Title';
import { Avatar } from '~/domains/design-system/Avatar';
import { Button } from '~/domains/design-system/Button';
import type { UserEntrant } from '~/domains/contest/domains/services/interfaces/entrant.interface';
import { useCommissionerChannelByUserId } from '~/domains/commissioner/hooks/useCommissionerChannelByUserId';
import { Routes } from '~/domains/common/constants/routes';
import { UserContext } from '~/components/providers/UserProvider';
import type { PaginatedData } from '~/domains/services/interfaces/paginated-data.interface';
import type { GetChannelResponse } from '~/domains/commissioner/interfaces/channel.interface';

import { useEntrantFollow } from '../../../../detail/components/Entrants/useEntrantFollow';

import { formatEntrantsNumber } from './utils/formatEntrantsNumber';

interface EntrantDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entrant: Partial<UserEntrant> | null;
  contestId: string;
  source: 'gamecenter_standings_slates' | 'contest_detail_entrants' | 'upcoming_entrants';
}

const useStyles = createStyles((theme) => ({
  dialog: {
    minWidth: 420,
    maxWidth: 420,
  },
  statsContainer: {
    width: '100%',
    flex: 3,
    padding: `${theme.other.spacing._24}px ${theme.other.spacing._16}px`,
    height: 94,
  },
  buttonsContainer: {
    width: '100%',
    marginTop: theme.other.spacing._8,
    display: 'flex',
    gap: theme.other.spacing._12,
  },
  buttonFlex: {
    flex: 1,
  },
  divider: {
    width: '100%',
    height: '1px',
    backgroundColor: theme.colors.gray2[0],
    margin: `${theme.other.spacing._16}px 0`,
  },
  avatar: {
    flex: 1,
    display: 'flex',
  },
  description: {
    wordBreak: 'break-word',
  },
}));

function EntrantDetailModal({
  isOpen,
  onClose,
  entrant,
  source,
  contestId,
}: EntrantDetailModalProps) {
  const { classes, theme } = useStyles();
  const { t } = useTranslation('contest');
  const router = useRouter();
  const { user } = useContext(UserContext);
  const queryClient = useQueryClient();

  const { follow, unfollow } = useEntrantFollow({
    source,
    contestId,
  });

  const { data: channelData, isLoading: isChannelLoading } = useCommissionerChannelByUserId({
    userId: entrant?.id || '',
  });

  if (!entrant) return null;
  const getShouldFollowButton = () => {
    const cachedData = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
      'contestEntrantsInfinite',
      contestId,
      source,
    ]);

    const cachedEntrant = cachedData?.pages
      ?.flatMap((page) => page.data)
      ?.find((e) => e.id === entrant.id);

    if (!cachedEntrant) {
      const cachedChannelData = queryClient.getQueryData<GetChannelResponse>([
        'commissioner/channel',
        entrant?.id,
      ]);

      return !cachedChannelData?.data?.isFollowed ?? channelData?.data?.isFollowed;
    }

    const isFollowing = cachedEntrant?.isFollowing;

    return !isFollowing;
  };

  const shouldShowFollowButton = getShouldFollowButton();
  const currentUserEntrant = entrant?.id === user?.id;

  const getRelationshipStatus = (entrantToCheck: Partial<UserEntrant>): ReactNode => {
    if (!entrantToCheck) return null;

    const cachedData = queryClient.getQueryData<{ pages: PaginatedData<UserEntrant>[] }>([
      'contestEntrantsInfinite',
      contestId,
      source,
    ]);
    const cachedEntrant = cachedData?.pages
      ?.flatMap((page) => page.data)
      ?.find((e) => e.id === entrantToCheck.id);

    const isFollowing = cachedEntrant?.isFollowing ?? entrantToCheck.isFollowing;

    if (isFollowing && entrantToCheck.isFollowed) {
      return (
        <Badge tt="none" bg={theme.colors.gray2[0]}>
          <Text variant="subhead-x-small" color={theme.colors.gray2[4]}>
            {t('common.entrants.list.mutuallyFollowing')}
          </Text>
        </Badge>
      );
    }

    if (isFollowing) {
      return (
        <Badge tt="none" bg={theme.colors.gray2[0]}>
          <Text variant="subhead-x-small" color={theme.colors.gray2[6]}>
            {t('common.entrants.list.following')}
          </Text>
        </Badge>
      );
    }

    if (entrantToCheck.isFollowed) {
      return (
        <Badge tt="none" bg={theme.colors.gray2[0]}>
          <Text variant="subhead-x-small" color={theme.colors.gray2[6]}>
            {t('common.entrants.list.followsYou')}
          </Text>
        </Badge>
      );
    }

    return null;
  };

  const displayEntrants = formatEntrantsNumber(channelData?.data?.entriesCount ?? 0);
  const displayFollowers = formatEntrantsNumber(channelData?.data?.followersCount ?? 0);
  const displayFollowing = formatEntrantsNumber(channelData?.data?.followingCount ?? 0);

  return (
    <DialogContainer isOpen={isOpen} onClose={onClose} lockScroll>
      <Dialog
        type={{
          desktop: 'modal',
          mobile: 'drawer',
        }}
        isOpen={isOpen}
        onClose={onClose}
        className={classes.dialog}
      >
        <DialogBody>
          <Flex align="center" justify="center">
            <div className={classes.avatar}>
              <Avatar radius="xl" size={64} src={entrant?.avatarUrl} />
            </div>
            <Flex className={classes.statsContainer} justify="space-between">
              <Flex direction="column" align="center" gap={theme.other.spacing._4}>
                {isChannelLoading ? (
                  <Skeleton height={24} width={40} radius="md" />
                ) : (
                  <Text variant="headline-large">{displayEntrants}</Text>
                )}
                <Text variant="body-medium" color={theme.colors.gray[6]}>
                  {t('common.entrants.list.entrants')}
                </Text>
              </Flex>
              <Divider orientation="vertical" color={theme.colors.gray2[0]} />
              <Flex direction="column" align="center" gap={theme.other.spacing._4}>
                {isChannelLoading ? (
                  <Skeleton height={24} width={40} radius="md" />
                ) : (
                  <Text variant="headline-large">{displayFollowers}</Text>
                )}
                <Text variant="body-medium" color={theme.colors.gray[6]}>
                  {t('common.entrants.list.followers')}
                </Text>
              </Flex>
              <Divider orientation="vertical" color={theme.colors.gray2[0]} />
              <Flex direction="column" align="center" gap={theme.other.spacing._4}>
                {isChannelLoading ? (
                  <Skeleton height={24} width={40} radius="md" />
                ) : (
                  <Text variant="headline-large">{displayFollowing}</Text>
                )}
                <Text variant="body-medium" color={theme.colors.gray[6]}>
                  {t('common.entrants.list.following')}
                </Text>
              </Flex>
            </Flex>
          </Flex>

          <Stack mih={{ base: 'auto', md: 200 }} spacing={theme.other.spacing._8}>
            <Group mih={30} spacing={theme.other.spacing._6}>
              {isChannelLoading ? (
                <Skeleton height={24} width={40} radius="md" />
              ) : (
                <Title variant="headline-xl">{channelData?.data?.name || entrant?.handle}</Title>
              )}
              {getRelationshipStatus(entrant)}
            </Group>
            <Text variant="body-large" color={theme.colors.gray2[3]}>
              @{entrant?.handle}
            </Text>

            <Text variant="body-large" className={classes.description}>
              {channelData?.data?.description}
            </Text>
          </Stack>
          <Flex mt={theme.other.spacing._12} gap={theme.other.spacing._12}>
            {shouldShowFollowButton && !currentUserEntrant && (
              <Button
                className={classes.buttonFlex}
                variant="secondary-fill"
                size="medium"
                onClick={(e) => {
                  follow.handleFollow(entrant.id, entrant.channelId || channelData?.data?.id, e);
                }}
                disabled={follow.isLoading || isChannelLoading}
              >
                {t('common.entrants.list.action')}
              </Button>
            )}
            {!shouldShowFollowButton && !currentUserEntrant && (
              <Button
                className={classes.buttonFlex}
                variant="secondary-stroke"
                size="medium"
                onClick={(e) => {
                  unfollow.handleUnfollow(
                    entrant.id,
                    entrant.channelId || channelData?.data?.id,
                    e
                  );
                }}
                disabled={unfollow.isLoading || isChannelLoading}
              >
                {t('common.entrants.list.unfollow')}
              </Button>
            )}

            <Button
              className={classes.buttonFlex}
              variant="secondary-stroke"
              size="medium"
              onClick={() => {
                router.push(Routes.channel(channelData?.data?.handle));
                onClose();
              }}
            >
              {t('common.entrants.list.viewChannel')}
            </Button>
          </Flex>
        </DialogBody>
      </Dialog>
    </DialogContainer>
  );
}

export default EntrantDetailModal;
